import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import {
  GlobalCompany,
  TeamSettingFormSchema,
  TeamSettings,
  TeamSettingSection,
  TeamSettingsFormSchema
} from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  AvatarComponent,
  ButtonComponent,
  CheckboxComponent,
  InlineFormSchemaComponent,
  ObjectCardComponent,
  SlideToggleComponent
} from '@mds/ui';
import { GetInitialsPipe } from '@pipes/get-initials.pipe';
import { AuthApiService } from '@services/auth-api.service';
import { ComposerApiService } from '@services/composer-api.service';
import { EPDManagementService } from '@services/epd-management.service';
import { GenericApiService } from '@services/generic-api.service';
import { GlobalSearchApiService } from '@services/global-search-api.service';
import { TeamAdminApiService } from '@services/team-admin-api.service';
import { ScipConfigService } from '@services/scip-config.service';
import { ToastrService } from '@services/toaster.service';
import { merge, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../../confirmation-dialog/confirmation-dialog.component';
import { PredictionModel } from './team-settings-tab.model';

const EMAIL_PATTERN = '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+.[A-Za-z]{2,4}$';

@Component({
  selector: 'app-team-settings-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatDialogModule,
    ButtonComponent,
    ButtonComponent,
    SlideToggleComponent,
    CheckboxComponent,
    ObjectCardComponent,
    MdsIconsModule,
    GetInitialsPipe,
    AvatarComponent,
    InlineFormSchemaComponent
  ],
  templateUrl: './team-settings-tab.component.html',
  styleUrls: ['../../team-common.scss', './team-settings-tab.component.scss']
})
export class TeamSettingsTabComponent implements OnInit {
  private globalSearchApiService = inject(GlobalSearchApiService);
  private genericApiService = inject(GenericApiService);
  private epdManagementService = inject(EPDManagementService);
  private composerApiService = inject(ComposerApiService);
  private teamAdminApiService = inject(TeamAdminApiService);
  private authApiService = inject(AuthApiService);
  private scipConfigService = inject(ScipConfigService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  @Input() section!: TeamSettingSection;
  @Input() epdSection!: TeamSettingSection;
  @Input() isUserTeamAdmin!: boolean;
  @Input() teamRid!: string;
  @Input() teamName!: string;
  @Input() isEditable: boolean;

  @Output() close = new EventEmitter<void>();

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput1', { static: false }) fileInput1!: ElementRef<HTMLInputElement>;
  @ViewChild('companyAutocomplete') companyAutocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  icons = MdsIcon;
  customerName = new FormControl('');
  companyControl: FormControl = new FormControl();
  scipRslControl: FormControl = new FormControl();
  selectedScipRsl: any;
  scipRslList: any[];
  showScipRsl: TeamSettingFormSchema;
  suggestions: GlobalCompany[] = [];
  isLoading = false;

  showDefaultCompany = false;
  ipcScrapingEnableForm: TeamSettingsFormSchema;
  ecoDesignEnableForm: TeamSettingsFormSchema;
  isEcodesignEnabled: boolean;
  selectedCompany: any;
  companyPreviousValue = null;
  currencyField: any;
  currencyOptions: string[] = [];
  managersEmail: string;
  projectManagerEmailSetting: any;
  emailSettingForm: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)])
  });

  filteredCurrency: Observable<string[]>;

  pcfQuestionnairesSetting: TeamSettingsFormSchema;
  pcfQuestionnairesEnum: { name: string; value: string }[];
  pcfQuestionnairesForm: FormGroup;
  pcfQuestionnairesOrder: number[] = [];

  supplierDatasetSchema: TeamSettingsFormSchema;
  selectedSupplierDataset: FormControl = new FormControl();

  rslFormSchema: TeamSettingsFormSchema;
  rslFormLine;
  rslFormValue: string[] = [];

  supplyChainAlternativesSchema: TeamSettingsFormSchema;
  supplyChainAlternativesControl: FormControl = new FormControl();

  skipDefaultAlternativeSchema: TeamSettingsFormSchema;
  skipDefaultAlternative: FormControl = new FormControl();

  chemicalAIForPredictions: TeamSettingsFormSchema;
  chemicalAIForPredictionsForm: FormControl = new FormControl();

  aiExiobasePredictions: TeamSettingsFormSchema;
  aiExiobasePredictionsControl: FormControl = new FormControl();

  scope3UserSchema: TeamSettingsFormSchema;
  scope3UserControl: FormControl = new FormControl();
  scope3UserOptions: any[] = [];
  selectedScope3User;
  previousScope3User;

  scope3DashboardEnabledForm: TeamSettingFormSchema;
  isScope3DashboardEnabled: boolean;

  enablePrimaryDataOverwriteForm: TeamSettingFormSchema;
  enablePrimaryDataOverwrite: FormControl = new FormControl(false);

  companyLogoFormSchema: TeamSettingsFormSchema;
  selectedFile: File | null = null;
  previewFile: string;

  inlineManual = '';

  azureInformation = '';
  page = 1;

  isIpcScrapingEnable: FormControl = new FormControl();

  listenerAdded = false;

  myControl = new UntypedFormControl('');

  hasMoreSuggestions = false;
  searchQuery = '';

  genericAiPredictAuxiliaries: TeamSettingFormSchema;
  genericAiPredictAuxiliariesForm = new FormControl();

  closestMatchEnrichment: FormControl = new FormControl();
  closestMatchEnrichmentForm: TeamSettingsFormSchema;
  questionnaireAttachmentEnabled: boolean;
  questionnaireAttachmentOptionalForm: TeamSettingsFormSchema;
  exemptionMaxConcentrationEnabled: boolean;
  exemptionMaxConcentrationMandatoryForm: TeamSettingsFormSchema;

  customerTaxonomyFormSchema: TeamSettingsFormSchema | null = null;
  customerTaxonomyForm: FormGroup = new FormGroup({});
  octopartEnrichmentForm: TeamSettingFormSchema;
  downStreamComplianceForm: TeamSettingFormSchema;
  isDownStreamEnabled: boolean;
  isOctopartEnrichEnabled: boolean;
  ipcLiveFeedComplianceForm: TeamSettingFormSchema;
  isIPCLiveFeedCompliance: boolean;
  includeWIAMProductForm: TeamSettingFormSchema;
  includeWIAMProduct: boolean;

  limitPartPredictionFormSchema: TeamSettingsFormSchema;
  limitPartPrediction: FormControl = new FormControl();

  isIMDSSupplierGeoFormSchema: TeamSettingsFormSchema;
  isIMDSSupplierGeo: FormControl = new FormControl();

  discreteAlternativesForm: TeamSettingFormSchema;
  isDiscreteAlternativesEnabled: boolean;

  showUserShareImport: TeamSettingsFormSchema;
  userControl: FormControl = new FormControl();
  optionsList: any[];
  userPreviousValue = null;

  skipAssigningDefaultRoutesSchema: TeamSettingsFormSchema;
  skipAssigningDefaultRoutesForm: FormControl = new FormControl();
  selectedUser: any;

  optOutOfUXTrackingFormSchema: TeamSettingsFormSchema;
  isOptOutOfUXTrackingEnabledFormControl: FormControl = new FormControl();

  genericAiFormSchema: TeamSettingsFormSchema;
  genericAiFormControl: FormControl = new FormControl();

  alternativeAiFormSchema: TeamSettingsFormSchema;
  alternativeAiFormControl: FormControl = new FormControl();

  auxiliariesAiFormSchema: TeamSettingsFormSchema;
  auxiliariesAiFormControl: FormControl = new FormControl();

  excludePrivateDataFormSchema: TeamSettingsFormSchema;
  excludePrivateDataFormControl: FormControl = new FormControl();

  excludePrivateAndPublicDataFormSchema: TeamSettingsFormSchema;
  excludePublicAndPremiumFormControl: FormControl = new FormControl();

  eClassAiFormSchema: TeamSettingsFormSchema;
  eClassAiFormControl: FormControl = new FormControl();

  forecastScenarioSchema: TeamSettingFormSchema;
  forecastScenarioControl: FormControl = new FormControl();
  forecastScenarioOptions: any[] = [];
  selectedForecastScenario;
  previousForecastScenario = null;

  predictionModelOptions = [
    {
      label: 'V1.0',
      value: PredictionModel.v1
    },
    {
      label: 'V2.0 (Beta)',
      value: PredictionModel.v2
    }
  ];

  predictionModelValue: FormControl = new FormControl(PredictionModel.v1);
  confidenceScore: FormControl = new FormControl<number | null>(null, [
    Validators.required,
    Validators.min(1),
    Validators.max(100)
  ]);

  get isCompanySelected(): boolean {
    return this.companyControl.value && typeof this.companyControl.value === 'object';
  }

  get isUserSelected(): boolean {
    return this.userControl.value && typeof this.userControl.value === 'object';
  }

  get isScipRslSelected(): boolean {
    return this.scipRslControl.value && typeof this.scipRslControl.value === 'object';
  }

  get isScope3UserSelected(): boolean {
    return this.scope3UserControl.value && typeof this.scope3UserControl.value === 'object';
  }

  get isForecastScenarioSelected(): boolean {
    return this.forecastScenarioControl.value && typeof this.forecastScenarioControl.value === 'object';
  }

  private parseBoolean = (value: any): boolean => typeof value === 'string' && value.toLowerCase() === 'true';

  companySettings() {
    this.companyControl.valueChanges
      .pipe(
        debounceTime(300), // Delay API call by 300ms
        distinctUntilChanged(),
        filter((p) => {
          if (typeof p === 'object') {
            return false;
          }
          return true;
        }),
        tap((query) => {
          this.page = 1; // Reset page when query changes
          this.searchQuery = query;
          this.suggestions = []; // Reset suggestions
          this.hasMoreSuggestions = true;
          this.isLoading = true;
        }),
        switchMap((query) =>
          this.globalSearchApiService.fetchCompanies(query, this.page).pipe(
            catchError((err) =>
              of({
                results: [],
                search: []
              })
            )
          )
        )
      )
      .subscribe((val) => {
        this.suggestions = val.results;
        if (this.selectedCompany) {
          const sCompany = this.suggestions.find((company) => company.name === this.selectedCompany.display_value);
          this.companyControl.setValue(sCompany, { emitEvent: false, emitModelToViewChange: true });
          this.autocompleteTrigger?.closePanel();
          this.selectedCompany = null;
          this.companyPreviousValue = sCompany;
        }
        this.isLoading = false;
        this.addPanelListener();
      });
  }

  fetchScipRsl() {
    this.scipRslControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((p) => {
          return !p || typeof p === 'object' ? false : true;
        }),
        tap((query) => {
          this.isLoading = true;
        }),
        switchMap((query) =>
          this.genericApiService.getBaseData(this.showScipRsl.options + '/' + query).pipe(
            catchError((err) => {
              this.isLoading = false;
              return of([]);
            })
          )
        )
      )
      .subscribe((res: any[]) => {
        this.isLoading = false;
        this.scipRslList = res || [];
        this.addPanelListener();
      });
  }

  fetchScope3User() {
    this.scope3UserControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((p) => typeof p !== 'object'),
        tap(() => {
          this.scope3UserOptions = [];
          this.isLoading = true;
        }),
        switchMap((query) =>
          this.teamAdminApiService.getTeamMembers(query).pipe(
            catchError(() => {
              this.isLoading = false;
              return of([]);
            })
          )
        )
      )
      .subscribe((res: any) => {
        if (Array.isArray(res.results)) {
          this.scope3UserOptions = res.results;
        }

        if (this.selectedScope3User) {
          const sUser = this.scope3UserOptions.find((user) => user.email === this.selectedScope3User.display_value);
          this.scope3UserControl.setValue(sUser, { emitEvent: false, emitModelToViewChange: true });
          this.autocompleteTrigger?.closePanel();
          this.selectedScope3User = null;
          this.previousScope3User = sUser;
        }

        this.isLoading = false;
      });
  }

  fetchForecastScenarios() {
    this.forecastScenarioControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((p) => typeof p !== 'object'),
        tap(() => {
          this.forecastScenarioOptions = [];
          this.isLoading = true;
        }),
        switchMap((query) => {
          const baseUrl = this.forecastScenarioSchema?.options || 'forecasting/search?filter=scenario_forecast';
          const url = query ? `${baseUrl}/${query}` : baseUrl;
          return this.genericApiService.getBaseData(url).pipe(
            catchError(() => {
              this.isLoading = false;
              return of([]);
            })
          );
        })
      )
      .subscribe((res: any) => {
        this.forecastScenarioOptions = res?.results || res || [];

        if (this.selectedForecastScenario) {
          const selected = this.forecastScenarioOptions.find(
            (s) => s.name === this.selectedForecastScenario.display_value
          );
          if (selected) {
            this.forecastScenarioControl.setValue(selected, { emitEvent: false });
            this.autocompleteTrigger?.closePanel();
            this.selectedForecastScenario = null;
            this.previousForecastScenario = selected;
          }
        }

        this.isLoading = false;
      });
  }

  updateCustomerName() {
    if (!this.customerName.getRawValue()) return;
    const obj = {
      company_name: this.customerName.getRawValue()
    };
    this.updateSettings(obj, 'customerName');
  }

  displayWithFn(option: GlobalCompany) {
    return option?.name;
  }

  toggle(key: string, value) {
    this.updateSettings({ [key]: value });
  }

  removeTile() {
    this.companyControl.setValue(null);
  }

  addPanelListener() {
    setTimeout(() => {
      const element = this.companyAutocomplete?.panel?.nativeElement;
      if (element) {
        this.listenerAdded = true;
        element.addEventListener('scroll', this.onPanelOpen.bind(this));
      }
    }, 500);
  }

  onPanelOpen(element: any) {
    const tracker = element.target;
    const limit = (tracker.scrollHeight - tracker.clientHeight) * 0.6;
    if (tracker.scrollTop >= limit && this.hasMoreSuggestions) {
      this.loadMore();
    }
  }

  loadMore(): void {
    if (!this.hasMoreSuggestions || this.isLoading) return;
    this.page++;
    this.isLoading = true;
    this.globalSearchApiService.fetchCompanies(this.searchQuery, this.page).subscribe((response) => {
      this.isLoading = false;
      if (response.results.length === 0) {
        this.hasMoreSuggestions = false;
      } else {
        this.suggestions = [...this.suggestions, ...response.results];
      }
    });
  }

  ngOnInit(): void {
    this.companySettings();
    this.fetchScipRsl();
    this.fetchUser();
    this.fetchScope3User();
    this.fetchForecastScenarios();

    this.getCompanyLogoForm(this.epdSection as TeamSettings);

    const { formSchema, lines } = this.section as { formSchema: TeamSettingFormSchema[]; lines: any };

    const keys = [
      'default_company_rid',
      'scip_rsl_rid',
      'ipc_scraping_enabled',
      'ecodesign_skip_premium',
      'enable_bom_import_octopart_enrichment',
      'enable_downstream_compliance',
      'auxiliaries_ai_predict_part_with_direct_material',
      'imds_use_supplier_geo',
      'enable_ipc_live_feed_compliance',
      'preferred_currency',
      'include_wiam_in_compliance',
      'service_user_to_share_bom_import_with_team',
      'company_name',
      'generic_ai_predict_auxiliaries',
      'create_closest_match_alternatives',
      'use_chemical_ai_for_prediction',
      'questionnaire_attachment_optional',
      'exemption_max_concentration_mandatory',
      'generic_ai_enabled',
      'alternatives_ai_enabled',
      'auxiliaries_ai_enabled',
      'ai_predict_exiobase_sector',
      'opt_out_of_ux_tracking',
      'rsl_overview',
      'disallow_private_name_based_default_alternatives',
      'disallow_public_name_based_default_alternatives',
      'default_scope3_user_rid',
      'scope3_dashboard_enabled',
      'eclass_prediction_enabled',
      'is_discrete_alternatives_normalization_disabled',
      'generic_ai_prediction_model',
      'generic_ai_min_conf_score',
      'overwrite_primary_data',
      'default_forecast_scenario_rid'
    ];
    const formMap: any = formSchema.reduce((form, item) => {
      if (keys.includes(item.key)) form[item.key] = item;
      return form;
    }, {});

    const lineMap = lines.reduce((acc, item) => {
      if (!Array.isArray(item) && keys.includes(item.key)) {
        acc[item.key] = item.value;
      }
      return acc;
    }, {});
    this.showDefaultCompany = !!formMap.default_company_rid;
    this.scope3UserSchema = formMap.default_scope3_user_rid;
    this.ecoDesignEnableForm = formMap.ecodesign_skip_premium;
    this.octopartEnrichmentForm = formMap.enable_bom_import_octopart_enrichment;
    this.downStreamComplianceForm = formMap.enable_downstream_compliance;
    this.scope3DashboardEnabledForm = formMap.scope3_dashboard_enabled;
    this.includeWIAMProductForm = formMap.include_wiam_in_compliance;
    this.currencyField = formMap.preferred_currency;
    this.showUserShareImport = formMap.service_user_to_share_bom_import_with_team;
    this.ipcLiveFeedComplianceForm = formMap.enable_ipc_live_feed_compliance;
    this.rslFormSchema = {
      ...formMap.rsl_overview,
      fieldtype: 'string'
    };
    this.rslFormLine = lines.find((line) => line.key === 'rsl_overview');

    this.showScipRsl = formMap.scip_rsl_rid;
    this.selectedScipRsl = lineMap.scip_rsl_rid;
    if (this.selectedScipRsl) {
      this.setDataSet(this.selectedScipRsl, this.scipRslControl);
    }

    this.isEcodesignEnabled = lineMap.ecodesign_skip_premium;
    this.isOctopartEnrichEnabled = lineMap.enable_bom_import_octopart_enrichment;
    this.isDownStreamEnabled = lineMap.enable_downstream_compliance;
    this.isScope3DashboardEnabled = lineMap.scope3_dashboard_enabled;
    this.isIPCLiveFeedCompliance = lineMap.enable_ipc_live_feed_compliance;
    this.includeWIAMProduct = lineMap.include_wiam_in_compliance;
    const customerName = lineMap.company_name;
    this.customerName.setValue(customerName);
    this.questionnaireAttachmentOptionalForm = formMap.questionnaire_attachment_optional;
    this.questionnaireAttachmentEnabled = lineMap.questionnaire_attachment_optional;
    this.exemptionMaxConcentrationMandatoryForm = formMap.exemption_max_concentration_mandatory;
    this.exemptionMaxConcentrationEnabled = lineMap.exemption_max_concentration_mandatory;
    this.selectedCompany = lines.find((e) => e.key === 'default_company_rid');
    this.selectedScope3User = lines.find((e) => e.key === 'default_scope3_user_rid');

    this.discreteAlternativesForm = formMap.is_discrete_alternatives_normalization_disabled;
    this.isDiscreteAlternativesEnabled = lineMap.is_discrete_alternatives_normalization_disabled;

    if (this.selectedCompany) {
      localStorage.setItem('default_company_rid', JSON.stringify(this.selectedCompany));
      this.companyControl.setValue(this.selectedCompany.display_value);
    } else {
      this.companyPreviousValue = null;
    }

    if (this.selectedScope3User) {
      this.scope3UserControl.setValue(this.selectedScope3User.display_value);
    } else {
      this.previousScope3User = null;
    }

    this.forecastScenarioSchema = formMap.default_forecast_scenario_rid;
    this.selectedForecastScenario = lines.find((e) => e.key === 'default_forecast_scenario_rid');
    if (this.selectedForecastScenario) {
      this.forecastScenarioControl.setValue(this.selectedForecastScenario.display_value);
    } else {
      this.previousForecastScenario = null;
    }

    localStorage.setItem('customer_name', customerName);
    this.handlePredictExiobaseWithAi(formMap, lineMap);
    this.currencyField = formSchema.find((e) => e.key === 'preferred_currency');
    this.currencyOptions = this.currencyField?.enum;
    const preferredCurrency = lineMap.preferred_currency;
    this.myControl.setValue(preferredCurrency);

    this.filteredCurrency = this.myControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filter(value || ''))
    );
    this.getPreferredRoutesSetting(this.section as TeamSettings);
    this.setClosestMatchEnrichment(formMap, lineMap);
    this.setIpcScraping(formMap, lineMap);
    this.handleAiPredictAuxiliaries(formMap, lineMap);
    this.getCustomerTaxonomyFormData(this.section as TeamSettings);
    this.getProductManagersDetails(this.section);
    this.getPCFSettingFormData(this.section);
    this.getSupplierDataset(this.section as TeamSettings);
    this.getDefaultAlternativeSetting(this.section as TeamSettings);
    this.getSupplyChainDefaultAlternativeSetting(this.section as TeamSettings);
    this.setChemicalAIPredictions(formMap, lineMap);
    this.setDataForShareBomImportWithTeam(lineMap);
    this.optOutOfUXTrackingFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'opt_out_of_ux_tracking',
      this.isOptOutOfUXTrackingEnabledFormControl
    );
    this.genericAiFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'generic_ai_enabled',
      this.genericAiFormControl
    );
    this.setGenericAISetting(formMap, lineMap);
    this.alternativeAiFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'alternatives_ai_enabled',
      this.alternativeAiFormControl
    );
    this.auxiliariesAiFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'auxiliaries_ai_enabled',
      this.auxiliariesAiFormControl
    );

    this.excludePrivateDataFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'disallow_private_name_based_default_alternatives',
      this.excludePrivateDataFormControl
    );
    this.excludePrivateAndPublicDataFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'disallow_public_name_based_default_alternatives',
      this.excludePublicAndPremiumFormControl
    );
    this.eClassAiFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'eclass_prediction_enabled',
      this.eClassAiFormControl
    );

    this.enablePrimaryDataOverwriteForm = this.setToggleFormField(
      formMap,
      lineMap,
      'overwrite_primary_data',
      this.enablePrimaryDataOverwrite
    );

    this.limitPartPredictionFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'auxiliaries_ai_predict_part_with_direct_material',
      this.limitPartPrediction
    );

    this.isIMDSSupplierGeoFormSchema = this.setToggleFormField(
      formMap,
      lineMap,
      'imds_use_supplier_geo',
      this.isIMDSSupplierGeo
    );
  }

  setGenericAISetting(formMap, lineMap) {
    this.genericAiFormSchema = formMap['generic_ai_enabled'];
    if (!this.genericAiFormSchema) return;

    const isEnabled = lineMap['generic_ai_enabled'] ?? this.parseBoolean(this.genericAiFormSchema?.defaultValue);

    this.genericAiFormControl.setValue(isEnabled, { emitEvent: false });
    this.predictionModelValue.setValue(lineMap['generic_ai_prediction_model'], { emitEvent: false });
    this.confidenceScore.setValue(lineMap['generic_ai_min_conf_score'], { emitEvent: false });

    this.syncUIState(isEnabled, this.predictionModelValue.value);

    merge(
      this.genericAiFormControl.valueChanges,
      this.predictionModelValue.valueChanges,
      this.confidenceScore.valueChanges.pipe(debounceTime(800))
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef) // Prevent memory leaks
      )
      .subscribe(() => {
        const isAiEnabled = this.genericAiFormControl.value;
        const currentModel = this.predictionModelValue.value;

        if (isAiEnabled) {
          if (!currentModel) {
            this.predictionModelValue.setValue(PredictionModel.v1, { emitEvent: false });
          }
        } else {
          this.predictionModelValue.setValue(null, { emitEvent: false });
          this.confidenceScore.setValue(null, { emitEvent: false });
        }

        if (this.predictionModelValue.value === PredictionModel.v1) {
          this.confidenceScore.setValue(null, { emitEvent: false });
        }
        this.syncUIState(isAiEnabled, this.predictionModelValue.value);
        this.saveGenericAIData();
      });
  }

  private syncUIState(isEnabled: boolean, model: PredictionModel): void {
    if (!isEnabled) {
      this.predictionModelValue.disable({ emitEvent: false });
      this.confidenceScore.disable({ emitEvent: false });
      return;
    }
    this.predictionModelValue.enable({ emitEvent: false });
    if (model === PredictionModel.v2) {
      this.confidenceScore.enable({ emitEvent: false });
    } else {
      this.confidenceScore.disable({ emitEvent: false });
    }
    this.predictionModelValue.updateValueAndValidity({ emitEvent: false });
    this.confidenceScore.updateValueAndValidity({ emitEvent: false });
  }

  private saveGenericAIData(): void {
    const modelValue = this.predictionModelValue.value;
    const scoreValue = this.confidenceScore.value;

    // Validation: Don't save if V2 is selected but score is missing
    if (
      modelValue === PredictionModel.v2 &&
      (scoreValue === null || scoreValue === undefined || scoreValue <= 0 || scoreValue > 100)
    ) {
      return;
    }

    const payload = {
      generic_ai_enabled: this.genericAiFormControl.value,
      generic_ai_prediction_model: modelValue,
      generic_ai_min_conf_score: scoreValue ? scoreValue.toString() : null
    };

    this.updateSettings(payload);
  }

  setDataSet(rid, formControl) {
    this.composerApiService.getTileCards(rid).subscribe((res) => {
      formControl.setValue(res[0], {
        emitEvent: false
      });
    });
  }

  setDataForShareBomImportWithTeam(linesMap: any) {
    if (this.showUserShareImport) {
      this.selectedUser = linesMap.service_user_to_share_bom_import_with_team;
      if (this.selectedUser) {
        this.composerApiService.getTileCards(this.selectedUser).subscribe((res) => {
          this.userControl.setValue(res[0]?.created_by ?? res[0], {
            emitEvent: false,
            emitModelToViewChange: true
          });
          this.autocompleteTrigger?.closePanel();
          this.selectedUser = null;
          this.userPreviousValue = res[0]?.created_by ?? res[0];
        });
      }
    }
  }

  /**
   * Initializes and populates the customer taxonomy form.
   * @param data - The customer taxonomy form data from API.
   */
  getCustomerTaxonomyFormData(data: TeamSettings): void {
    const taxonomyKey = 'customer_taxonomy_name';
    const taxonomyValue = data.lines.find((e: any) => e.key === taxonomyKey)?.value || '';

    this.customerTaxonomyFormSchema = data.formSchema.find((e: any) => e.key === taxonomyKey);

    if (!this.customerTaxonomyFormSchema) return; // Exit if schema is not found

    this.customerTaxonomyFormSchema.defaultValue = taxonomyValue;
    this.customerTaxonomyForm.addControl(taxonomyKey, new FormControl(taxonomyValue, Validators.required));
  }

  handlePredictExiobaseWithAi(formSchemaMap, linesMap) {
    const key = 'ai_predict_exiobase_sector';
    this.aiExiobasePredictions = formSchemaMap[key];
    if (this.aiExiobasePredictions) {
      const aiExiobaseValue = !!linesMap[key];
      this.aiExiobasePredictionsControl.setValue(aiExiobaseValue, { emitEvent: false });
      this.aiExiobasePredictionsControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.updateSettings({ [key]: value });
      });
    }
  }

  setAiPredictAuxiliariesDefaultValue() {
    if (
      this.genericAiPredictAuxiliariesForm?.value == null &&
      this.parseBoolean(this.genericAiPredictAuxiliaries?.defaultValue)
    ) {
      this.genericAiPredictAuxiliariesForm.setValue(true, { emitEvent: false });
    }
  }

  handleAiPredictAuxiliaries(formMap, lineMap) {
    const key = 'generic_ai_predict_auxiliaries';
    this.genericAiPredictAuxiliaries = formMap[key];
    if (this.genericAiPredictAuxiliaries) {
      const lineData = !!lineMap[key];
      this.genericAiPredictAuxiliariesForm.setValue(lineData, { emitEvent: false });

      this.setAiPredictAuxiliariesDefaultValue();

      this.genericAiPredictAuxiliariesForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.updateSettings({ [key]: value });
      });
    }
  }

  private setChemicalAIPredictions(formMap, lineMap) {
    const key = 'use_chemical_ai_for_prediction';
    this.chemicalAIForPredictions = formMap[key];
    if (this.chemicalAIForPredictions) {
      const lineData = lineMap[key];
      this.chemicalAIForPredictionsForm.setValue(lineData, { emitEvent: false });
      this.setDefaultValue(this.chemicalAIForPredictions, this.chemicalAIForPredictionsForm);

      this.chemicalAIForPredictionsForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.updateSettings({ [key]: value });
      });
    }
  }

  setDefaultValue(schemaField: TeamSettingsFormSchema, form: FormControl) {
    if (form?.value == null && this.parseBoolean(schemaField?.defaultValue)) {
      form.setValue(true, { emitEvent: false });
    }
  }

  updateSettings(formData, from?: string, key?: string, formControl?: FormControl) {
    const payload = {
      key: this.teamRid,
      formData
    };
    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe({
      next: () => {
        if (from === 'customerName') {
          localStorage.setItem('customer_name', this.customerName.value);
        }
        this.toastr.showMessage('Updated Successfully');
      },
      error: (err) => {
        // rollback value on failure
        if (key && formControl) {
          formControl.setValue(!formData[key], { emitEvent: false });
          this.toastr.showMessage(err?.error?.message || 'Failed to update. Value reverted.');
        }
      }
    });
  }

  setClosestMatchEnrichment(formMap, lineMap) {
    const key = 'create_closest_match_alternatives';
    this.closestMatchEnrichmentForm = formMap.create_closest_match_alternatives;
    if (this.closestMatchEnrichmentForm) {
      this.closestMatchEnrichment.setValue(lineMap.create_closest_match_alternatives);
      // Set Default Value
      if (
        this.closestMatchEnrichment?.value == null &&
        this.parseBoolean(this.closestMatchEnrichmentForm?.defaultValue)
      ) {
        this.closestMatchEnrichment.setValue(true, { emitEvent: false });
      }
      this.closestMatchEnrichment.valueChanges.subscribe((value) => {
        this.updateSettings({ [key]: value });
      });
    }
  }

  setIpcScraping(formMap, lineMap) {
    const key = 'ipc_scraping_enabled';
    this.ipcScrapingEnableForm = formMap.ipc_scraping_enabled;
    if (this.ipcScrapingEnableForm) {
      this.isIpcScrapingEnable.setValue(lineMap.ipc_scraping_enabled);
      // Set Default Value
      if (this.isIpcScrapingEnable?.value == null && this.parseBoolean(this.ipcScrapingEnableForm?.defaultValue)) {
        this.isIpcScrapingEnable.setValue(true, { emitEvent: false });
      }
      this.isIpcScrapingEnable.valueChanges.subscribe((value) => {
        this.updateSettings({ [key]: value });
      });
    }
  }

  filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.currencyOptions.filter((option: any) => option.name.toLowerCase().includes(filterValue));
  }

  /**
   * This method init managers email form and fill it with lines value
   * @param data - section data that contain formschema and lines data
   */
  getProductManagersDetails(data) {
    this.managersEmail = data.lines.find((e) => e.key === 'project_manager_email')?.value || '';
    this.projectManagerEmailSetting = data.formSchema.find((e) => e.key === 'project_manager_email');
    this.emailSettingForm.setValue({
      email: this.managersEmail
    });
  }

  /**
   * Processes the PCF settings form data, initializes the form controls, and handles value changes.
   * @param data - The data containing the form schema and initial values for PCF settings.
   */
  getPCFSettingFormData(data: TeamSettingSection): void {
    // Initialize PCF settings and options
    this.pcfQuestionnairesSetting = data.formSchema.find((e) => e.key === 'pcf_questionnaire_sections_to_display');
    this.pcfQuestionnairesEnum = [...(this.pcfQuestionnairesSetting?.enum || [])];
    this.pcfQuestionnairesOrder = [...Array(this.pcfQuestionnairesEnum.length).keys()].map((i) => i + 1);

    function getPcfDefaultValues(lines: any[]): string[] {
      return lines.find((e) => e.key === 'pcf_questionnaire_sections_to_display')?.value || [];
    }

    if (!this.pcfQuestionnairesSetting) return;

    // Extract initial values for the controls
    const initialValueOfPcf: string[] = getPcfDefaultValues(data.lines);

    const formControls: { [key: string]: FormGroup } = {};
    this.pcfQuestionnairesEnum.forEach((item) => {
      const isChecked = initialValueOfPcf.includes(item.value);

      const group = this.fb.group({
        checkbox: new FormControl(isChecked),
        dropdown: new FormControl({ value: initialValueOfPcf.indexOf(item.value) + 1, disabled: !isChecked })
      });

      group
        .get('checkbox')
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((isCheckboxChecked) => {
          this.pcfSectionValidation(item.value, group, isCheckboxChecked);
        });

      group
        .get('dropdown')
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.resetDuplicatePcfOrder(item.value, value);
        });

      formControls[item.value] = group;
    });
    this.pcfQuestionnairesForm = this.fb.group(formControls);
  }

  rslOnChanged(e): void {
    if (e.value.Inputs.rsl_overview) {
      this.rslFormValue = e.value.Inputs.rsl_overview.map((x) => x.rid);
    }
  }

  updateRSL(): void {
    this.updateSettings({ rsl_overview: this.rslFormValue });
  }

  /**
   * Retrieves the supplier dataset schema based on the given key and sets the selected dataset value.
   * Subscribes to value changes and updates settings accordingly.
   *
   * @param {TeamSettings} data - The team settings object containing form schema and dataset values.
   */
  getSupplierDataset(data: TeamSettings): void {
    const key = 'material_breakdown_dataset_search_scope';

    this.supplierDatasetSchema = data.formSchema.find((e) => e.key === key);
    if (!this.supplierDatasetSchema) return;

    // Retrieve the selected dataset value or use the default value if not found
    const selectedDataset =
      data.lines.find((item) => item.key === key)?.value ?? this.supplierDatasetSchema.defaultValue;

    this.selectedSupplierDataset.setValue(selectedDataset, { emitEvent: false });
    this.selectedSupplierDataset.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.updateSettings({ [key]: value }));
  }

  /**
   * Initializes the Skip Default Alternative setting control:
   *  - Finds the schema for the "skip_assigning_default_alternatives" key
   *  - Sets the form control value from line data or defaults from schema
   *  - Subscribes to value changes and auto-saves setting updates
   *
   * @param data TeamSettings object containing schema and current values
   */
  getDefaultAlternativeSetting(data: TeamSettings): void {
    const key = 'skip_assigning_default_alternatives';

    // Locate the schema definition for the given setting key
    this.skipDefaultAlternativeSchema = data.formSchema.find((e) => e.key === key);
    if (!this.skipDefaultAlternativeSchema) return;

    // Get the current value from data; fallback on schema's default if not present
    const selectedDataset =
      data.lines.find((item) => item.key === key)?.value ??
      String(this.skipDefaultAlternativeSchema.defaultValue).toLowerCase() === 'true';

    // Set value to the form control without emitting change events
    this.skipDefaultAlternative.setValue(selectedDataset, { emitEvent: false });

    // Subscribe to form control value changes, update settings on change
    this.skipDefaultAlternative.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.updateSettings({ [key]: value });
    });
  }

  getSupplyChainDefaultAlternativeSetting(data: TeamSettings): void {
    const key = 'unset_default_alternatives_for_nonleaf_pg';

    this.supplyChainAlternativesSchema = data.formSchema.find((e) => e.key === key);
    if (!this.supplyChainAlternativesSchema) return;

    const previousSavedValue =
      data.lines.find((item) => item.key === key)?.value ??
      String(this.supplyChainAlternativesSchema.defaultValue).toLowerCase() === 'true';

    this.supplyChainAlternativesControl.setValue(previousSavedValue, { emitEvent: false });

    this.supplyChainAlternativesControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.updateSettings({ [key]: value });
    });
  }

  pcfSectionValidation(key: string, group: FormGroup, isChecked: boolean): void {
    const formControls = this.pcfQuestionnairesForm.controls;
    const selectedSections = this.getPCFSelectedValues();

    // Ensure at least one section remains selected
    if (!isChecked && selectedSections.length === 0) {
      group.get('checkbox')?.setValue(true, { emitEvent: false });
      this.toastr.showMessage('At least one section must be selected.', 4000);
      return;
    }

    // Enable/disable dropdowns based on checkbox values
    Object.values(formControls).forEach(({ controls: { checkbox, dropdown } }: FormGroup) => {
      checkbox.value ? dropdown.enable() : dropdown.disable();
    });

    // Disable all dropdowns if only one section is selected
    if (selectedSections.length === 1) {
      Object.values(formControls).forEach(({ controls: { dropdown } }: FormGroup) => dropdown.disable());
    }
  }

  getPCFSelectedValues(): string[] {
    const formValues = this.pcfQuestionnairesForm.getRawValue();
    return Object.keys(formValues)
      .filter((key) => formValues[key].checkbox)
      .sort((a, b) => {
        const dropdownA = formValues[a].dropdown;
        const dropdownB = formValues[b].dropdown;

        return dropdownA && !dropdownB ? -1 : !dropdownA && dropdownB ? 1 : dropdownA - dropdownB;
      });
  }

  resetDuplicatePcfOrder(key: string, order: number): void {
    if (!order) return;

    Object.entries(this.pcfQuestionnairesForm.controls).forEach(([controlKey, control]) => {
      const dropdown = control.get('dropdown');
      if (dropdown?.value === order && key !== controlKey) {
        dropdown.setValue('', { emitEvent: false });
        this.toastr.showMessage('Duplicate orders are not allowed. Each section must have a unique order.', 4000);
      }
    });
  }

  /**
   * Handles the file selection event for uploading a company logo.
   * @param event - The file input change event.
   */
  onCompanyLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];

    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.showMessage('Invalid file type. Please upload a .jpg, .jpeg, .png, or .svg file.', 5000);
      return;
    }

    this.selectedFile = file;

    const reader = new FileReader();
    reader.onloadend = () => {
      this.previewFile = reader.result as string;
    };
    reader.readAsDataURL(this.selectedFile);
  }

  /**
   * Opens a confirmation dialog and deletes the company logo if confirmed.
   */
  deleteCompanyLogo(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Delete Company Logo',
        text: 'Are you sure you want to delete the company logo?',
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef), // Ensures unsubscription on component destroy
        filter((confirm) => !!confirm), // Proceeds only if the user confirms deletion
        switchMap(() => this.epdManagementService.deleteCompanyLogo(this.teamRid))
      )
      .subscribe({
        next: () => {
          this.removeLocalFile();
          this.companyLogoFormSchema.defaultValue = null;
          this.toastr.showMessage('Company logo deleted successfully.', 5000);
        },
        error: (error) => {
          this.toastr.showMessage(error?.error?.message || 'An error occurred while deleting the logo.', 5000);
        }
      });
  }

  updateInlineJS() {
    let identifier = window.location.host.split('.')[0];
    if (identifier.includes('localhost')) {
      identifier = this.teamName ? this.teamName : 'new-dev';
    } else {
      identifier = this.teamName;
    }

    this.teamAdminApiService.updateInlineManual(identifier, this.inlineManual).subscribe({
      next: () => {
        this.toastr.showMessage('Inline manual updated successfully', 1000);
      },
      error: () => {}
    });
  }

  updateAzureInformation() {
    this.authApiService.uploadAzure(this.azureInformation).subscribe({
      next: () => {
        this.toastr.showMessage('Azure information updated successfully', 1000);
      },
      error: () => {}
    });
  }

  toggleIpcScraping(toggleValue: boolean) {
    const obj = {
      ipc_scraping_enabled: toggleValue
    };
    this.updateSettings(obj);
  }

  toggleEcodesignProducts(ecoDesignValue: boolean) {
    const obj = {
      ecodesign_skip_premium: ecoDesignValue
    };
    this.updateSettings(obj);
  }

  toggleScope3Dashboard(value: boolean) {
    const payload = { key: this.teamRid, formData: { scope3_dashboard_enabled: value } };
    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe({
      next: () => this.toastr.showMessage('Updated Successfully'),
      error: (err) => {
        this.isScope3DashboardEnabled = !value;
        this.toastr.showMessage(err?.error?.message || 'Failed to update. Value reverted.');
      }
    });
  }

  submitPCFSection() {
    const result = this.getPCFSelectedValues();
    this.updateSettings({ pcf_questionnaire_sections_to_display: result });
  }

  submitCurrency() {
    if (!this.myControl?.value) {
      this.toastr.showMessage('Currency not selected!', 2000);
      return;
    }
    const payload = { [this.currencyField.key]: this.myControl?.value };
    this.updateSettings(payload);
  }

  /**
   * This method used for submit managers email, call composer route put api with payload
   */
  onSubmitEmailSetting() {
    if (this.emailSettingForm.value.email === this.managersEmail) return;
    const payload = {
      [this.projectManagerEmailSetting.key]: this.emailSettingForm.value.email
    };
    this.updateSettings(payload);
  }

  contactSupport() {
    this.closeDialog();
  }

  closeDialog() {
    this.close.emit();
  }

  deactivateTeam() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Delete team',
        text: `Are you sure you want to delete team ${this.teamName} ?`,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.teamAdminApiService.deactivateTeam(this.teamRid).subscribe(() => {
          this.toastr.showMessage('Delete team successfully', 1000);
          this.closeDialog();
          this.router.navigate(['/login']);
        });
      }
    });
  }

  updateCompany() {
    const currentCompanyValue = this.companyControl.value ?? null;
    if (
      (!currentCompanyValue && this.companyPreviousValue === currentCompanyValue) ||
      currentCompanyValue?.rid === this.companyPreviousValue?.rid
    ) {
      this.toastr.showMessage('This option is already selected. Please choose a different one.', 4000);
      return;
    }

    const payload = {
      default_company_rid: this.companyControl?.value?.rid.replace('#', '') ?? null
    };
    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe((value) => {
      this.toastr.showMessage('Updated Successfully', 2000);
      this.companyPreviousValue = this.companyControl.value;
    });
  }

  updateValue(control, formData) {
    const value = control?.value?.rid.replace('#', '') ?? null;
    const payload = {
      [formData?.key]: value
    };
    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe(() => {
      this.toastr.showMessage('Updated Successfully', 2000);
      if (formData?.key === 'scip_rsl_rid') {
        this.scipConfigService.invalidateCache();
      }
    });
  }

  /**
   * Initializes company logo form schema by retrieving the default logo URL.
   * @param data - The team settings containing the form schema and company logo details.
   */
  getCompanyLogoForm(data: TeamSettings): void {
    if (!data) return;
    const key = 'epd_company_logo_url';
    const schema = data.formSchema.find((e) => e.key === key);

    if (schema?.hidden) return; // if BE return hidden true then hide company logo
    this.companyLogoFormSchema = data.formSchema.find((e) => e.key === key);

    if (this.companyLogoFormSchema) {
      const imgsrc = data.lines.find((item) => item.key === key)?.value;

      if (imgsrc) {
        this.epdManagementService.fetchCompanyLogoAsBlob(imgsrc as string).subscribe((blob: Blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const url = reader.result as string;
            this.companyLogoFormSchema.defaultValue = url;
          };
          // converts to Base64 string to use in img-src
          reader.readAsDataURL(blob);
        });
      }
    }
  }

  /**
   * Uploads the selected company logo to the server.
   */
  protected uploadCompanyLogo(): void {
    if (!this.selectedFile) {
      this.toastr.showMessage('Please select a valid image file first.', 5000);
      return;
    }

    this.epdManagementService
      .uploadCompanyLogo(this.teamRid, this.selectedFile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: { url: string }) => {
          this.updateCompanyLogo(res.url);
          // Reset file input
          if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
          }
        },
        error: (error) => {
          this.toastr.showMessage(error?.error?.message || 'An error occurred during file upload.', 5000);
        }
      });
  }

  /**
   * Updates the company logo URL in the system.
   * @param logoUrl - The URL of the uploaded company logo.
   */
  private updateCompanyLogo(logoUrl: string): void {
    const payload = {};
    payload[this.companyLogoFormSchema.key] = logoUrl;

    this.epdManagementService
      .updateCompanyLogo(this.teamRid, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.toastr.showMessage('Uploaded Successfully');
          this.companyLogoFormSchema.defaultValue = this.previewFile;

          this.removeLocalFile();
          const imgsrc = res?.updated?.epd_company_logo_url;
          if (imgsrc) {
            this.epdManagementService.fetchCompanyLogoAsBlob(imgsrc).subscribe((blob: Blob) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const url = reader.result as string;
                this.companyLogoFormSchema.defaultValue = url;
              };
              reader.readAsDataURL(blob); // converts to Base64 string to use in img-src
            });
          }
        },
        error: (error) => {
          this.toastr.showMessage(error?.error?.message || 'An error occurred while updating the logo.', 5000);
        }
      });
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  openFilePicker1() {
    this.fileInput1.nativeElement.click();
  }

  removeLocalFile() {
    this.previewFile = null;
    this.selectedFile = null;
  }

  /**
   * Submits the customer taxonomy form if the value has changed.
   */
  onSubmitCustomerTaxonomy(): void {
    const formValue = this.customerTaxonomyForm.value;
    const taxonomyKey = this.customerTaxonomyFormSchema?.key;

    if (!taxonomyKey || formValue[taxonomyKey] === this.customerTaxonomyFormSchema.defaultValue) return; // Exit if unchanged

    this.updateSettings(formValue);
  }

  removeUserTile() {
    this.userControl.setValue(null);
  }

  removeScope3UserTile() {
    this.scope3UserControl.setValue(null);
  }

  onPanelFocus() {
    setTimeout(() => {
      this.addPanelListener();
    }, 1000);
  }

  fetchUser() {
    this.userControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((p) => {
          if (!p || typeof p === 'object') {
            return false;
          }
          return true;
        }),
        tap((query) => {
          this.optionsList = [];
          this.isLoading = true;
        }),
        switchMap((query) =>
          this.teamAdminApiService.getTeamMembers(query).pipe(
            catchError((err) => {
              this.isLoading = false;
              return of([]);
            })
          )
        )
      )
      .subscribe((res: { results: [] }) => {
        this.optionsList = res.results || [];
        this.isLoading = false;
      });
  }

  updateUser() {
    const currentUserValue = this.userControl.value ?? null;
    if (
      (!currentUserValue && this.userPreviousValue === currentUserValue) ||
      currentUserValue?.rid === this.userPreviousValue?.rid
    ) {
      this.toastr.showMessage('This user is already selected. Please choose a different one.', 4000);
      return;
    }

    const payload = {
      key: this.teamRid,
      formData: {
        service_user_to_share_bom_import_with_team: this.userControl?.value?.rid.replace('#', '') ?? null
      }
    };
    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe((value) => {
      this.toastr.showMessage('Updated Successfully', 3000);
      this.userPreviousValue = this.userControl.value;
    });
  }

  updateScope3User() {
    const currentScope3UserValue = this.scope3UserControl.value ?? null;

    if (
      (!currentScope3UserValue && this.previousScope3User === currentScope3UserValue) ||
      currentScope3UserValue?.rid === this.previousScope3User?.rid
    ) {
      this.toastr.showMessage(
        currentScope3UserValue
          ? 'This user is already selected. Please choose a different one.'
          : 'Please select a user',
        4000
      );
      return;
    }

    const payload = {
      key: this.teamRid,
      formData: {
        default_scope3_user_rid: currentScope3UserValue?.rid.replace('#', '') ?? null
      }
    };

    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe(() => {
      this.toastr.showMessage('Updated Successfully', 3000);
      this.previousScope3User = this.scope3UserControl.value;
    });
  }

  updateForecastScenario() {
    const currentValue = this.forecastScenarioControl.value ?? null;

    if (
      (!currentValue && this.previousForecastScenario === currentValue) ||
      currentValue?.rid === this.previousForecastScenario?.rid
    ) {
      this.toastr.showMessage(
        currentValue ? 'This scenario is already selected. Please choose a different one.' : 'Please select a scenario',
        4000
      );
      return;
    }

    const payload = {
      key: this.teamRid,
      formData: {
        default_forecast_scenario_rid: currentValue?.rid?.replace('#', '') ?? null
      }
    };

    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe({
      next: () => {
        this.toastr.showMessage('Updated Successfully', 3000);
        this.previousForecastScenario = this.forecastScenarioControl.value;
      },
      error: (err) => {
        this.toastr.showMessage(err?.error?.message || 'Failed to update forecast scenario.', 4000);
      }
    });
  }

  removeForecastScenarioTile() {
    this.forecastScenarioControl.setValue(null);
  }

  getPreferredRoutesSetting(data: TeamSettings): void {
    const key = 'skip_assigning_preferred_routes_in_boms';

    // Find the schema definition for the setting key
    this.skipAssigningDefaultRoutesSchema = data.formSchema.find((schema) => schema.key === key);
    if (!this.skipAssigningDefaultRoutesSchema) return;

    // Determine the initial value:
    // Use the value from data.lines if available, otherwise fallback to the schema's default value
    const lineItem = data.lines.find((item) => item.key === key);
    const selectedValue =
      lineItem?.value ?? String(this.skipAssigningDefaultRoutesSchema.defaultValue).toLowerCase() === 'true';

    // Initialize the form control value without emitting change events
    this.skipAssigningDefaultRoutesForm.setValue(selectedValue, {
      emitEvent: false
    });

    // React to form value changes and persist the updated value to settings
    this.skipAssigningDefaultRoutesForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.updateSettings({ [key]: value });
    });
  }

  setToggleFormField(formMap, lineMap, key: string, fieldControl: FormControl) {
    const fieldFormSchema = formMap[key];
    if (fieldFormSchema) {
      fieldControl.setValue(lineMap[key]);
      if (fieldControl?.value == null && this.parseBoolean(fieldFormSchema?.defaultValue)) {
        fieldControl.setValue(true, { emitEvent: false });
      }
      fieldControl.valueChanges.subscribe((value) => {
        this.updateSettings({ [key]: value }, null, key, fieldControl);
      });
    }
    return fieldFormSchema;
  }
}
